/**
 * The consistency report, as the page reads it.
 *
 * Mirrors packages/server/src/types/labConsistency.ts. Everything here is a verbatim
 * projection of a recorded harness run - no model writes any part of it, because the
 * panels are exactly what is being graded for run-to-run variance.
 */

export type RunStep = 'basic' | 'updated';

export type AssessmentSection = 'differentials' | 'setAside' | 'tests' | 'overall';

export type AssessmentRating = 'sound' | 'minor' | 'questionable' | 'unsafe';

/** What each rating means to the veterinarian choosing it. */
export const RATING_LABELS: Record<AssessmentRating, string> = {
  sound: 'Clinically sound',
  minor: 'Minor issues',
  questionable: 'Questionable',
  unsafe: 'Wrong or unsafe',
};

export const SECTION_LABELS: Record<AssessmentSection, string> = {
  differentials: 'Proposed diagnoses',
  setAside: 'Set aside',
  tests: 'Suggested follow-up labs',
  overall: 'Overall',
};

/** One datapoint and how often it appeared. */
export interface SectionItem {
  id: number;
  name: string;
  runs: number;
  /** 1-based run indexes that carried it. */
  presentIn: number[];
  meanRank: number | null;
}

export interface SectionStability {
  runs: number;
  /**
   * Whether the section appeared at all, run to run - `max(p, 1-p)`.
   *
   * Held apart from content because Jaccard cannot express it: two empty sets score
   * 1.0 in it and empty-against-populated scores 0.0, so "consistently offers nothing"
   * and "offers something half the time" are the same number. The second is the defect
   * a veterinarian actually reported.
   */
  presenceAgreement: number;
  contentAgreement: number;
  leadAgreement: number;
  items: SectionItem[];
}

/**
 * What the veterinarian actually saw, in their terms.
 *
 * Not "did the pipeline succeed". Whether a case record existed, whether the engine
 * ranked nothing, whether the review excluded everything - all of that is plumbing and
 * produces one identical screen, so it is counted as one outcome and the reason is kept
 * out of the score.
 *
 * `noDifferential` is not a defect. A panel with nothing out of range SHOULD produce no
 * diagnosis. Whether it was right on a given report is a clinical judgement, and asking
 * for that judgement is what this page is for.
 */
export type RunOutcome = 'differential' | 'noDifferential' | 'notDiagnostic';

export interface OutcomeAgreement {
  runs: number;
  differential: number;
  noDifferential: number;
  notDiagnostic: number;
  agreement: number;
  majority: RunOutcome;
  reasons: string[];
  noDifferentialReasons: Record<string, number>;
  /** The report's own shape, so "nothing" can be judged rather than assumed wrong. */
  analyteCount: number | null;
  abnormalCount: number | null;
}

export const OUTCOME_LABELS: Record<RunOutcome, string> = {
  differential: 'Differential produced',
  noDifferential: 'No differential',
  notDiagnostic: 'Sample not diagnostic',
};

export interface BatchHeader {
  batchId: string;
  generatedAt: string;
  publishedAt: string;
  publishedBy: string;
  label: string;
  gitSha: string | null;
  envSource: string;
  targetEnv: string;
  reportNos: string[];
  steps: RunStep[];
  runsPerReport: number;
  runCount: number;
  failedRunCount: number;
}

export interface ReportScore {
  batchId: string;
  reportNo: string;
  step: RunStep;
  runs: number;
  failedRuns: number;
  outcome: OutcomeAgreement;
  differentials: SectionStability;
  setAside: SectionStability;
  tests: SectionStability;
  engineDifferentials: SectionStability;
  engineSetAside: SectionStability;
  contentHash: string;
}

export interface RunPanels {
  batchId: string;
  reportNo: string;
  step: RunStep;
  run: number;
  startedAt: string | null;
  elapsedMs: number;
  error: string | null;
  caseId: string | null;
  caseStatus: string | null;
  stage: string | null;
  outcome: RunOutcome;
  noDifferentialReason: string | null;
  notDiagnosticReason: string | null;
  analyteCount: number | null;
  abnormalCount: number | null;
  verdict: string | null;
  limits: string | null;
  sections: Array<{ title: string; text: string; analyteIds: string[] }>;
  differentials: Array<{ diagnosisId: number; name: string; rank: number; summary: string }>;
  setAside: Array<{ diagnosisId: number; name: string; reason: string; whatWouldSettleIt: string | null }>;
  tests: Array<{ serviceNo: number; name: string; price: number | null; settles: string | null }>;
  nextSteps: string[];
  assumedPatientFields: string[];
  symptoms: Array<{ id: number; name: string }>;
  engineDiagnoses: Array<{ id: number; name: string }>;
  toolsCalled: string[];
}

export interface Assessment {
  batchId: string;
  reportNo: string;
  step: RunStep;
  section: AssessmentSection;
  veterinarianId: number;
  veterinarianEmail: string;
  veterinarianName: string;
  rating: AssessmentRating;
  comment: string;
  assessedContentHash: string;
  createdAt: string;
  updatedAt: string;
}

export interface BatchDetail {
  batch: BatchHeader;
  reports: ReportScore[];
  assessments: Assessment[];
}

export interface ReportDetail {
  report: ReportScore;
  runs: RunPanels[];
  assessments: Assessment[];
  step: RunStep;
}

/**
 * The bar each measure is read against.
 *
 * Presence is held to 1.00 rather than 0.90 on purpose: a section that appeared in
 * nine runs of ten is still a section that vanished once, and the veterinarian who
 * saw the tenth has no way to know that. Content is where 0.90 is the honest target.
 */
export function gradeOf(value: number, metric: 'presence' | 'content'): 'good' | 'warn' | 'bad' {
  const target = metric === 'presence' ? 1 : 0.9;
  if (value >= target) return 'good';
  if (value >= target - 0.15) return 'warn';
  return 'bad';
}
